import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './assets/seance-grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import textFilterProblem from 'src/components/react-table/filters/text-filterProblem';
import { IInvoice } from 'src/types/invoice';
import { CellProps } from 'react-table';
import dateTimeFilter from 'src/components/react-table/filters/datetime-filter';
import SeanceActionsColumn from './SeanceActionsColumn';
import SeanceCellColumn from './SeanceCellColumn';
import SeanceCellEditColumn from './SeanceCellEditColumn';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import dropdownFilter from 'src/components/react-table/filters/dropdown-filter';
import CaseEnum from 'src/enumerables/case-enum';
import { getValFromEnum } from 'src/components/redux-form/utils/dropdown-values';
import { IServiceType } from 'src/types/service-types';
import OrderEnum from 'src/enumerables/order-enum';
import GeneralEnum from 'src/enumerables/general-enum';
import InvoiceEditActionsColumn from './InvoiceEditActionsColumn';

import InvoiceCellDetailColumn from './SeanceCellDetailColumn';
import { changeToZeroMoment } from '../../../../../../helpers/timeHelpers';
import { QueryParams } from '../../../../../../types/grid';
import { numberWithCommas } from '../../../../../../helpers/format';

interface Props {
    invoices: Paginated<IInvoice>;
    students: SelectObject[];
    vendors: SelectObject[];
    districts: SelectObject[];
    providers: SelectObject[];
    serviceTypes: Paginated<IServiceType>;
    stateData: any;
    paymentId: undefined | string | number;
    caseType: any;
    getData: (params: QueryParams) => void;
    caseTypeHistory?: any;
    amountValue: number;
    detail?: boolean;
    isCredit?: boolean;
    setStateData: (data: any) => void;
    changeValue: (field: string, value: string | null) => void;
}

const typeEnum = OrderEnum ? OrderEnum.getEnum('OrderType') : {};
const statusEnum = GeneralEnum ? GeneralEnum.getEnum('InvoiceStatus') : {};
const orderTypes: SelectObject[] = getValFromEnum(typeEnum);
const statuses: SelectObject[] = getValFromEnum(statusEnum);
const caseEnum = CaseEnum ? CaseEnum.getEnum('caseType') : {};
const caseTypes: SelectObject[] = getValFromEnum(caseEnum);
const getColumns = (
    data: any,
    setData: any,
    studentsData: any,
    providersData: any,
    vendorsData: any,

    districtsData: any,
    serviceTypesData: any,
    paymentId: undefined | string | number,
    amountValue: number,
    detail: boolean | undefined,
    isCredit: boolean | undefined,
    changeValue: (field: string, value: string | null) => void,
): Column<Partial<IInvoice>>[] => [
    {
        Header: 'Actions',
        accessor: 'isWeekly',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            if (paymentId) {
                return <InvoiceEditActionsColumn original={original} data={data} setData={setData} />;
            } else {
                return (
                    <SeanceActionsColumn key={Math.random() * 10} original={original} data={data} setData={setData} />
                );
            }
        },
        disableSortBy: true,
        disableFilters: true,
        width: '10%',
    },
    {
        Header: '#',
        accessor: 'id',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => (original.id ? `${+original.id}` : ''),
        Filter: textFilterProblem<IInvoice>(),
    },
    {
        Header: 'Invoice Date',
        accessor: 'createdAt[after]',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            const value: any = original.createdAt;
            return value ? `${changeToZeroMoment(value, 'MMM D, YYYY')}` : '';
        },
        Filter: dateTimeFilter<IInvoice>(),
    },
    {
        Header: 'Invoice Total',
        accessor: 'invoiceTotal',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => (original.invoiceTotal ? `$${numberWithCommas(original.invoiceTotal)}` : '$0.00'),
        Filter: textFilterProblem<IInvoice>(),
    },
    {
        Header: 'Actually Paid, $',
        accessor: 'remainingAmount',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            if (isCredit) {
                return (
                    <SeanceCellColumn
                        changeValue={changeValue}
                        amountValue={amountValue}
                        name={'remainingAmount'}
                        original={original}
                        data={data}
                        setData={setData}
                    />
                );
            }
            if (detail) {
                return (
                    <InvoiceCellDetailColumn
                        paymentId={paymentId}
                        name={'remainingAmount'}
                        original={original}
                        data={data}
                        amountValue={amountValue}
                        setData={setData}
                    />
                );
            }
            if (paymentId) {
                return (
                    <SeanceCellEditColumn
                        changeValue={changeValue}
                        paymentId={paymentId}
                        name={'remainingAmount'}
                        original={original}
                        data={data}
                        amountValue={amountValue}
                        setData={setData}
                    />
                );
            } else {
                return (
                    <SeanceCellColumn
                        changeValue={changeValue}
                        amountValue={amountValue}
                        name={'remainingAmount'}
                        original={original}
                        data={data}
                        setData={setData}
                    />
                );
            }
        },
        Filter: textFilter<IInvoice>(),
    },

    {
        Header: 'Case Number',
        disableSortBy: true,

        accessor: 'occasion.orderEntity.caseNumber',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => (
            <Link
                to={Router.generate(routes.CASE_EDIT, {
                    id: original?.occasion?.id,
                })}
            >
                {original.occasion?.caseNumber}
            </Link>
        ),
        Filter: textFilterProblem<IInvoice>(),
    },
    {
        Header: 'Provider',
        disableSortBy: true,

        accessor: 'provider.id',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            if (!original?.provider) return '';

            return (
                <Link
                    to={Router.generate(routes.PROVIDER_DETAIL, {
                        id: original?.provider?.id,
                        tab: 'main-info',
                        subId: '',
                    })}
                >
                    {original.provider?.fullName}
                </Link>
            );
        },
        Filter: dropdownFilter<IInvoice>(providersData),
    },
    {
        Header: 'Student',
        disableSortBy: true,
        //disableFilters: true,
        accessor: 'student.id',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            if (!original?.student) return '';

            return (
                <Link
                    to={Router.generate(routes.STUDENT_DETAIL, {
                        id: original?.student?.id,
                        tab: 'student-info',
                    })}
                >
                    {`${original.student?.firstName} ${original.student?.lastName}`}
                </Link>
            );
        },
        Filter: dropdownFilter<IInvoice>(studentsData),
    },
    {
        Header: 'Vendor',
        disableSortBy: true,
        disableFilters: true,
        accessor: 'vendor.id',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            if (!original?.vendor) return '';

            return (
                <Link
                    to={Router.generate(routes.VENDOR_DETAIL, {
                        id: original?.vendor?.id,
                    })}
                >
                    {`${original.vendor?.name}`}
                </Link>
            );
        },
        Filter: dropdownFilter<IInvoice>(vendorsData),
    },
    {
        Header: 'District',
        disableSortBy: true,
        disableFilters: true,
        accessor: 'district.id',

        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            if (!original?.district) return '';

            return (
                <Link
                    to={Router.generate(routes.DISTRICT_DETAIL, {
                        id: original?.district?.id,
                    })}
                >
                    {`${original.district?.name}`}
                </Link>
            );
        },
        Filter: dropdownFilter<IInvoice>(districtsData),
    },
    {
        Header: 'Service Type',
        accessor: 'updatedAt',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            let title = original.occasion?.serviceType?.title;
            if (!title) {
                title = original.occasion?.serviceType?.cpt;
            }

            return <div>{title}</div>;
        },
        disableSortBy: true,
        disableFilters: true,
        Filter: dropdownFilter<IInvoice>(serviceTypesData),
    },

    {
        Header: 'Service Period',
        accessor: 'periodStart[after]',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            const valueOrderType = original.periodStart;
            return valueOrderType ? `${changeToZeroMoment(valueOrderType, 'MMM D, YYYY')}` : '';
        },
        Filter: dateTimeFilter<IInvoice>(),
    },
    {
        Header: 'Case Type',
        accessor: 'caseType.id',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            const valueCaseType = caseTypes.find((it) => +it.value === original.occasion?.caseType);
            return valueCaseType ? valueCaseType.label : '';
        },
        Filter: textFilter<IInvoice>(),
    },
    {
        Header: 'Order Type',
        accessor: 'orderType.id',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            const valueOrderType = orderTypes.find((it) => it.value === original.occasion?.orderType);
            return valueOrderType ? valueOrderType.label : '';
        },
        Filter: dropdownFilter<IInvoice>(orderTypes),
    },

    {
        Header: 'Note',
        accessor: 'note',
        disableSortBy: true,
        disableFilters: true,
        Filter: textFilter<IInvoice>(),
    },
    {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IInvoice>) => {
            const valuestatus = statuses.find((it) => +it.value === +original.status);

            return valuestatus ? valuestatus.label : '';
        },
        Filter: dropdownFilter<IInvoice>(statuses),
    },
];

const InvoicePaymentGrid: React.FunctionComponent<Props> = ({
    students,
    isCredit,
    detail,
    providers,
    invoices,
    stateData,
    getData,
    caseTypeHistory,
    setStateData,
    districts,
    caseType,
    paymentId,
    vendors,
    serviceTypes,
    amountValue,
    changeValue,
}: Props) => {
    const serviceTypesVal: any = [];
    serviceTypes.list.forEach((st: Partial<IServiceType>) => {
        serviceTypesVal.push({ value: st.id, label: st.title ? st.title : st.cpt });
    });

    const columnData: Column<Partial<IInvoice>>[] = getColumns(
        stateData,
        setStateData,
        students,
        providers,
        vendors,
        districts,
        serviceTypesVal,
        paymentId,
        amountValue,
        detail,
        isCredit,
        changeValue,
    );

    const arrFilter: any[] = [];

    if (paymentId) {
        arrFilter.push({ id: 'occasion.caseType', value: +caseType });
    } else {
        arrFilter.push({ id: 'occasion.caseType', value: +caseTypeHistory.value });
    }

    /*if (+caseTypeHistory.value === 1 || caseType === 1) {
        arrFilter.push({ id: 'status', value: [5, 6, 9] });
    } else {
        arrFilter.push({ id: 'status', value: [1, 3, 4, 5, 6, 9] });
    }*/

    if (paymentId) {
        arrFilter.push({ id: 'paymentId', value: paymentId });
    }
    arrFilter.push({ id: 'fromPayment', value: 1 });

    return (
        <div className={classNames(styles.grid)}>
            <Grid<Partial<IInvoice>>
                noDots={true}
                classNameGrid={classNames(styles.gridStyle)}
                columns={columnData}
                filtersInit={arrFilter}
                data={invoices}
                getData={getData}
            />
        </div>
    );
};

export default InvoicePaymentGrid;
